<template>
  <div></div>
</template>

<script>
import { CHESS_SIDE } from "vue-chess-shared/constants";
import { GAME_TYPE, ONLINE_STATUS } from "@/constants";
import { mapActions, mapGetters } from "vuex";
import { firestore } from "@/services/firebase";
import { isNumeric } from "vue-chess-shared/utils/number-helper";
import { GameDataBuilder } from "vue-chess-shared/GameDataBuilder";
import { cachedTimeOffset } from "@/utils/firebase-helper";
import { getPlayerColor } from "@/utils/game-helper";
import { getCommencedAt } from "vue-chess-shared/utils/match-helper";
import { toDateWithOffset, diffIn } from "vue-chess-shared/utils/date-helper";

export default {
  name: "GameDataManager",

  inject: [
    "onDataManagerReady",
    "onGameDataReady",
    "onGameDataUpdated",
    "onGameSettingsUpdated",
  ],

  props: {
    matchId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      isReady: false,
      bindFirestore: null,
      gameSettings: {},
      player1_rating_current: null,
      player2_rating_current: null,
      player1_rating_new: null,
      player2_rating_new: null,
      gameEndedAtLoad: false,
    };
  },

  watch: {
    matchId: {
      handler() {
        this.isReadyEmitted = false;
        this.isReady = false;
      },
      immediate: true,
    },

    userId: {
      immediate: true,
      handler() {
        if (!this.userId) return;
        if (!this.matchId) return;

        this.handleUpdate();

        this.$bind(
          "gameSettings",
          firestore.collection("settings").doc("" + this.userId)
        );
      },
    },

    isReady() {
      if (!this.isReady) return;

      if (this.isReadyEmitted) {
        return;
      }

      if (!this.match.player1 || !this.match.player2) {
        return;
      }

      // keep current rating to display when there is no new rating for match such as resign before 4th move.
      this.player1_rating_current = this.match.player1.stats
        ? this.match.player1.stats.rating
        : null;
      this.player2_rating_current = this.match.player2.stats
        ? this.match.player2.stats.rating
        : null;

      // only emit onGameReady once per match
      this.isReadyEmitted = true;
      this.handleReady();
    },

    match: {
      handler() {
        if (!this.isReadyEmitted) {
          return;
        }

        // move's endedAt field using value: firebase.firestore.FieldValue.serverTimestamp(),
        // which will be null at creation, and will be available at a later time
        // so we don't need to deliver update in case endedAt is not yet available
        if (this.match.endedBy && !this.match.endedAt) return;

        this.handleUpdate();
      },
      deep: true,
    },

    moves() {
      if (!this.match) return;

      // move's createdAt field using value: firebase.firestore.FieldValue.serverTimestamp(),
      // which will be null at creation, so because we calculate moveStats, etc... using createdAt
      // it will not work correctly when createdAt is null,
      // So when createdAt is available later, we will handle the update
      if (!this.lastMove || !this.lastMove.createdAt) return;

      // move's receivedAt field using value: firebase.firestore.FieldValue.serverTimestamp(),
      // which will be null at creation, and will be available at a later time
      // so we don't need to deliver update in case receivedAt is not yet available
      if (this.lastMove.received && !this.lastMove.receivedAt) return;

      this.handleUpdate();
    },

    gameSettings() {
      this.handleUpdateSettings();
    },

    opponentStatus(newVal, oldValue) {
      if (newVal === oldValue) return;
      if (!this.match) return;
      this.handleUpdate();
    },
  },

  computed: {
    ...mapGetters({
      userId: "getUserId",
      onlinePlayers: "getOnlinePlayers",
    }),

    /**
     * @returns {CHESS_SIDE} the color of the user (white or black) who is viewing the page
     * the user must be one of the 2 players in the match
     * else return an empty string
     */
    playerColor() {
      return getPlayerColor(this.match, this.userId);
    },

    gameType() {
      if (this.match.player1.aiLevel && this.match.player2.aiLevel) {
        return GAME_TYPE.AIvAI;
      }

      if (!this.match.player1.aiLevel && !this.match.player2.aiLevel) {
        return GAME_TYPE.PvP;
      }

      return GAME_TYPE.PvAI;
    },

    opponent() {
      return this.playerColor === CHESS_SIDE.WHITE
        ? this.match.player2
        : this.match.player1;
    },

    opponentColor() {
      return this.playerColor === CHESS_SIDE.WHITE
        ? CHESS_SIDE.BLACK
        : CHESS_SIDE.WHITE;
    },

    opponentStatus() {
      if (!this.match) return null;
      if (!this.playerColor) return null;
      if (this.gameType != GAME_TYPE.PvP) return null;
      if (!this.onlinePlayers || !this.opponent) return null;
      if (this.opponent.user_id === this.userId) return null;
      const op = this.onlinePlayers.find(
        (p) => p.user_id === this.opponent.user_id
      );
      if (op) return op.status;
      return ONLINE_STATUS.OFFLINE;
    },

    lastMove() {
      return this.moves ? this.moves.at(-1) : null;
    },

    lastMoveTime() {
      // No move yet
      if (!this.lastMove) {
        if (!this.match.startedAt) return -1; // match hasn't started yet
        return (
          diffIn(
            toDateWithOffset(cachedTimeOffset()),
            getCommencedAt(this.match, this.moves),
            "second"
          ) + cachedTimeOffset()
        );
      }

      // Match finished
      if (this.match.endedAt) {
        return this.match.endedAt - this.lastMove.createdAt;
      }

      // new move created but firebase hasn't return createdAt yet
      if (!this.lastMove.createdAt) return 0;

      return (
        diffIn(
          toDateWithOffset(cachedTimeOffset()),
          this.lastMove.receivedAt || this.lastMove.createdAt,
          "second"
        ) + cachedTimeOffset()
      );
    },

    gameData() {
      if (!this.isReady) return {};
      if (!this.match) return {};

      const gameDataBuilder = new GameDataBuilder(
        this.match,
        this.moves,
        cachedTimeOffset()
      );
      const gameData = gameDataBuilder.gameData();

      return {
        ...gameData,
        matchId: this.matchId,
        playerColor: this.playerColor,
        opponent: this.opponent,
        opponentStatus: this.opponentStatus,
        gameType: this.gameType,
        bindFirestore: this.bindFirestore,
        player1_rating_new: this.player1_rating_new,
        player2_rating_new: this.player2_rating_new,
      };
    },
  },

  methods: {
    ...mapActions({
      saveGameCopy: "saveGameCopy",
    }),

    handleReady() {
      const gameData = this.gameData;
      this.updateRating(gameData);
      this.saveGameCopy({
        matchId: this.matchId,
        gameData: gameData,
      });
      this.onGameDataReady(this.gameData);
    },

    handleUpdate() {
      if (!this.match) return;

      const gameData = this.gameData;

      this.updateRating(gameData);
      this.updateTimeOffset(gameData.timeData);
      this.saveGameCopy({
        matchId: this.matchId,
        gameData: gameData,
      });
      this.onGameDataUpdated(gameData);
    },

    handleUpdateSettings() {
      this.onGameSettingsUpdated(this.gameSettings || {});
    },

    addMove(moveData) {
      const {
        orig,
        dest,
        color,
        moveNumber,
        move,
        fen,
        pgn,
        isPremove,
      } = moveData;

      const newMove = {
        player_id:
          color === CHESS_SIDE.WHITE
            ? this.match.player1.username
            : this.match.player2.username,
        color,
        moveNumber,
        move,
        orig,
        dest,
        fen,
        pgn,
        localTimestamp: toDateWithOffset(cachedTimeOffset()),
        isPremove,
      };

      this.createMove(newMove);
    },

    updateRating(gameData) {
      if (gameData.match) {
        // don't display rating diff initially
        if (gameData.match.player1 && gameData.match.player1.stats) {
          if (this.gameEndedAtLoad) {
            // game ended on load, don't display current rating and wait for new rating
            gameData.match.player1.stats.rating = null;
          }
          gameData.match.player1.stats.rating_diff = null;
        }

        if (gameData.match.player2 && gameData.match.player2.stats) {
          if (this.gameEndedAtLoad) {
            // game ended on load, don't display current rating and wait for new rating
            gameData.match.player2.stats.rating = null;
          }
          gameData.match.player2.stats.rating_diff = null;
        }
      }
    },

    updateTimeOffset(timeData) {
      if (!timeData) return;

      const firestoreTimeOffset = cachedTimeOffset();

      if (
        !this.match.result &&
        this.lastMove &&
        this.lastMove.color === this.playerColor
      ) {
        // If I made the last move, update my firestoreTimeOffset
        const newTimeOffset =
          timeData[this.playerColor].timeOffset || firestoreTimeOffset;

        console.debug(
          this.playerColor,
          "update firestoreTimeOffset",
          newTimeOffset
        );
        if (isNumeric(newTimeOffset)) {
          sessionStorage.setItem("firestoreTimeOffset", newTimeOffset);
        }
      }
    },
  },

  mounted() {
    if (!this.onDataManagerReady) {
      console.error("GameDataManager needs to be placed inside a GameBoard");
      return;
    }
    this.onDataManagerReady(this);
  },
};
</script>
